import { imageIdOf } from './autoImage.js';
import { textsOf } from './autoInstances.js';
import { cmpStr, displayOf, resolveFields, type StoryCtx } from './ctx.js';
import { DRAFT } from './defaults.js';
import { newSection } from './instantiate.js';
import { mentionsIn, plainRefs, refIndex } from './refs.js';
import { blockText, textBlocks, type TextBlock } from './textBlocks.js';
import { isScene, scenesOf } from './scene.js';
import { spansIn } from './spans.js';
import { effectiveTimeline, isDocked, nodeTimes, orderedItems, positionOf, timeBasisOf, timeValueOf } from './timeline.js';
import { childrenOf, descendants, visibleOn } from './tree.js';
import type { FieldValue, MaterialItem, NodeType, StoryNode, TextDocument } from './types.js';

/*
 * Erzählebene (Sujet): Manuskript → Abschnitte (optional) → Kapitel → Szenen (über ihr `chapterId`). Eine Szene ist zugleich ein Stück Weltzeit (Fabel):
 * ein Zeitpunkt oder Zeitraum, am Zeitstrahl oder lose (siehe `scene.ts`). Rückblenden und Vorausdeutungen ergeben sich aus der
 * Lesereihenfolge gegen die Weltzeit. Alles hier ist reine Berechnung aus den gespeicherten Daten.
 */

// ---------------------------------------------------------------------------
// Sortierschlüssel
// ---------------------------------------------------------------------------

const DIGITS = '0123456789abcdefghijklmnopqrstuvwxyz';
const BASE = DIGITS.length;
const digit = (s: string, i: number): number => DIGITS.indexOf(s[i]!);

/**
 * Sortierschlüssel zwischen zwei Nachbarn (`null` = Anfang bzw. Ende). Schlüssel sind Ziffernfolgen zur Basis 36, die wie
 * Nachkommastellen verglichen werden (also einfach als Text); erzeugte Schlüssel enden nie auf „0“, damit immer Platz davor bleibt.
 */
export function rankBetween(a: string | null, b: string | null): string {
  if (a !== null && b !== null && a >= b) throw new Error(`Sortierschlüssel: „${a}“ liegt nicht vor „${b}“.`);
  let prefix = '';
  let upperBound = b !== null;
  for (let i = 0; i < 64; i++) {
    const lo = a !== null && i < a.length ? digit(a, i) : 0;
    const hi = upperBound && b !== null ? (i < b.length ? digit(b, i) : 0) : BASE;
    if (hi - lo > 1) return prefix + DIGITS[(lo + hi) >> 1];
    // kein Platz an dieser Stelle: die Ziffer übernehmen und eine Stelle weiter suchen
    prefix += DIGITS[lo];
    if (hi - lo === 1) upperBound = false;
  }
  throw new Error('Sortierschlüssel: kein Platz mehr zwischen den Nachbarn.');
}

/** `count` gleichmäßig verteilte Sortierschlüssel in aufsteigender Reihenfolge (für viele Elemente auf einmal, z. B. die Datenübernahme). */
export function evenRanks(count: number): string[] {
  if (count <= 0) return [];
  let width = 1;
  while (BASE ** width < (count + 1) * 4) width++;
  const space = BASE ** width;
  return Array.from({ length: count }, (_, i) => {
    let v = Math.floor(((i + 1) * space) / (count + 1));
    let s = '';
    for (let w = 0; w < width; w++) {
      s = DIGITS[v % BASE] + s;
      v = Math.floor(v / BASE);
    }
    return s.replace(/0+$/, '');
  });
}

/** Geschwister nach Sortierschlüssel (fehlende zuletzt), bei Gleichstand nach Erstellung. */
export function byRank<T extends Pick<StoryNode, 'rank' | 'createdAt' | 'id'>>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    if (a.rank && b.rank && a.rank !== b.rank) return cmpStr(a.rank, b.rank);
    if (!a.rank !== !b.rank) return a.rank ? -1 : 1;
    return cmpStr(a.createdAt, b.createdAt) || cmpStr(a.id, b.id);
  });
}

/** Sortierschlüssel für ein neues Element am Ende der Geschwister (Abschnitte und Kapitel im Manuskript, Kapitel in einem Abschnitt, Szenen in einem Kapitel). */
export const rankAtEnd = (siblings: Pick<StoryNode, 'rank' | 'createdAt' | 'id'>[]): string => {
  const last = byRank(siblings.filter((s) => s.rank)).pop();
  return rankBetween(last?.rank ?? null, null);
};

// ---------------------------------------------------------------------------
// Lesereihenfolge
// ---------------------------------------------------------------------------

export const manuscriptOf = (ctx: StoryCtx): StoryNode | null => childrenOf(ctx, ctx.story.rootId).find((n) => n.kind === 'manuscript') ?? null;

export interface SceneEntry {
  scene: StoryNode;
  chapter: StoryNode;
  chapterNumber: number;
  /** Nummer der Szene im Kapitel (ab 1) */
  sceneNumber: number;
  /** „Kapitel.Szene“, z. B. „3.2“ */
  label: string;
  /** Stelle in der Lesereihenfolge (ab 0) */
  index: number;
}

export interface ChapterEntry {
  chapter: StoryNode;
  /** Nummer des Kapitels, fortlaufend über die Abschnitte hinweg */
  number: number;
  /** Abschnitt, in dem das Kapitel steht; null: direkt im Manuskript */
  section: StoryNode | null;
  scenes: SceneEntry[];
}

export interface SectionEntry {
  section: StoryNode;
  /** Nummer des Abschnitts (ab 1) */
  number: number;
  chapters: ChapterEntry[];
}

export interface NarrativeOrder {
  manuscript: StoryNode | null;
  /** die Abschnitte in Lesereihenfolge (auch leere) */
  sections: SectionEntry[];
  /** alle Kapitel in Lesereihenfolge, auch die in Abschnitten */
  chapters: ChapterEntry[];
  /** alle eingeordneten Szenen in Lesereihenfolge */
  scenes: SceneEntry[];
  /** Szenen ohne Kapitel („nicht eingeordnet“) in der Reihenfolge der Weltzeit (siehe `worldOrder`) */
  unassigned: StoryNode[];
}

/** Was direkt im Manuskript bzw. in einem Abschnitt steht und eine Stelle hat: im Manuskript Abschnitte und Kapitel, im Abschnitt Kapitel */
export const outlineChildren = (ctx: StoryCtx, parentId: string): StoryNode[] => {
  if (ctx.nodes.get(parentId)?.kind === 'section') return childrenOf(ctx, parentId).filter((c) => c.kind === 'chapter');
  return childrenOf(ctx, parentId).filter((c) => c.kind === 'chapter' || c.kind === 'section');
};

/**
 * Abschnitte, Kapitel und ihre Szenen in Lesereihenfolge, nummeriert (Kapitel fortlaufend über die Abschnitte hinweg); dazu die nicht
 * eingeordneten Szenen. Im Manuskript stehen Abschnitte und Kapitel ohne Abschnitt in einer Reihe.
 */
export function narrativeOrder(ctx: StoryCtx): NarrativeOrder {
  const manuscript = manuscriptOf(ctx);
  const sections: SectionEntry[] = [];
  const chapters: ChapterEntry[] = [];
  const scenes: SceneEntry[] = [];
  const all = scenesOf(ctx);
  const inChapter = new Map<string, StoryNode[]>();
  for (const s of all) if (s.chapterId) inChapter.set(s.chapterId, [...(inChapter.get(s.chapterId) ?? []), s]);
  const addChapter = (chapter: StoryNode, section: StoryNode | null): ChapterEntry => {
    const ci = chapters.length;
    const entry: ChapterEntry = { chapter, number: ci + 1, section, scenes: [] };
    byRank(inChapter.get(chapter.id) ?? []).forEach((scene, si) => {
      const se: SceneEntry = { scene, chapter, chapterNumber: ci + 1, sceneNumber: si + 1, label: `${ci + 1}.${si + 1}`, index: scenes.length };
      entry.scenes.push(se);
      scenes.push(se);
    });
    chapters.push(entry);
    return entry;
  };
  if (manuscript) {
    for (const n of byRank(outlineChildren(ctx, manuscript.id))) {
      if (n.kind === 'chapter') addChapter(n, null);
      else sections.push({ section: n, number: sections.length + 1, chapters: byRank(outlineChildren(ctx, n.id)).map((c) => addChapter(c, n)) });
    }
  }
  const placed = new Set(scenes.map((s) => s.scene.id));
  return { manuscript, sections, chapters, scenes, unassigned: worldOrder(ctx, all.filter((s) => !placed.has(s.id))) };
}

/**
 * Szenen in der Reihenfolge der Weltzeit, soweit sie sich ablesen lässt: erst alle mit Datum (auch über eine eigene Zählung mit
 * Nullpunkt) nach ihrem Beginn – über alle Zeitstrahle und lose Szenen hinweg –, dann die einer eigenen Zählung ohne Nullpunkt, dann
 * die angedockten ohne Zeitrechnung je Zeitstrahl (von oben nach unten) nach ihrer Lage, zuletzt der Rest nach Erstellung.
 */
export function worldOrder(ctx: StoryCtx, list: StoryNode[]): StoryNode[] {
  const timelines = new Map(
    childrenOf(ctx, ctx.story.rootId)
      .filter((n) => n.kind === 'timeline')
      .sort((a, b) => (a.y ?? 0) - (b.y ?? 0) || cmpStr(a.createdAt, b.createdAt) || cmpStr(a.id, b.id))
      .map((t, i) => [t.id, i]),
  );
  const keyOf = (n: StoryNode): { group: number; value: number } => {
    const w = worldSpanOf(ctx, n);
    if (!w) return { group: timelines.size + 2, value: 0 };
    if (w.basis === 'ms') return { group: 0, value: w.start };
    if (w.basis === 'count') return { group: 1, value: w.start };
    return { group: 2 + (timelines.get(w.basis) ?? timelines.size), value: w.start };
  };
  const keys = new Map(list.map((n) => [n.id, keyOf(n)]));
  return [...list].sort((a, b) => {
    const ka = keys.get(a.id)!;
    const kb = keys.get(b.id)!;
    return ka.group - kb.group || ka.value - kb.value || cmpStr(a.createdAt, b.createdAt) || cmpStr(a.id, b.id);
  });
}

/** Das Standarddokument „Draft“ einer Szene */
export const draftDocOf = (scene: StoryNode): TextDocument | undefined =>
  scene.documents.find((d) => d.locked && d.name === DRAFT) ?? scene.documents.find((d) => d.name.toLowerCase() === DRAFT.toLowerCase());

/** Der Manuskripttext einer Szene (Standarddokument „Draft“). */
export const draftOf = (scene: StoryNode): string => draftDocOf(scene)?.text ?? '';

/** Wörter eines Markdown-Textes; Verweise zählen als das Wort, für das sie stehen („Lisa[@lisa]“ = ein Wort). */
export function wordCount(markdown: string): number {
  return plainRefs(markdown).match(/[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*/gu)?.length ?? 0;
}

// ---------------------------------------------------------------------------
// Weltzeit einer Szene
// ---------------------------------------------------------------------------

/** Lage einer angedockten Szene auf ihrem Zeitstrahl (Zeitpunkt: `from` = `to`). Lose Szenen haben keine Lage (null). */
export function axisOf(ctx: StoryCtx, n: StoryNode): { timelineId: string; from: number; to: number } | null {
  if (!isDocked(ctx, n)) return null;
  const from = positionOf(n);
  return { timelineId: n.parentId!, from, to: n.kind === 'event' ? from : (n.to ?? from) };
}

export interface SceneWorldTime {
  /** Beginn und Ende (`JJJJ-MM-TTThh:mm`), sofern Datumsangaben da sind; bei einem Zeitpunkt ist das Ende der Beginn */
  start: string | null;
  end: string | null;
  /** Zeitstrahl der angedockten Szene und ihre Spanne darauf (Achsenwerte) */
  timelineId: string | null;
  from: number | null;
  to: number | null;
}

/** Weltzeit einer Szene: aus ihrer Lage am Zeitstrahl, lose aus ihren Zeitfeldern. */
export function sceneTime(ctx: StoryCtx, scene: StoryNode): SceneWorldTime {
  const t = nodeTimes(ctx, scene);
  const axis = axisOf(ctx, scene);
  return { start: t.start, end: t.end ?? t.start, timelineId: axis?.timelineId ?? null, from: axis?.from ?? null, to: axis?.to ?? null };
}

/**
 * Vergleichbare Spanne der Weltzeit einer Szene. Basis `ms`: Datum bzw. eigene Zählung mit Nullpunkt (Millisekunden, über alle
 * Zeitstrahle und lose Szenen hinweg); `count`: eigene Zählung ohne Nullpunkt (Minuten ab dem gemeinsamen Nullpunkt aller solchen
 * Zählungen); sonst die Id des Zeitstrahls (Lage darauf, ohne Zeitrechnung). Auf die Minute gerundet. null ohne Zeit.
 */
export interface WorldSpan {
  basis: 'ms' | 'count' | (string & {});
  start: number;
  end: number;
}

export function worldSpanOf(ctx: StoryCtx, scene: StoryNode): WorldSpan | null {
  const axis = axisOf(ctx, scene);
  if (axis) {
    const b = timeBasisOf(effectiveTimeline(ctx, ctx.nodes.get(axis.timelineId)!));
    if (!b) return { basis: axis.timelineId, start: axis.from, end: axis.to };
    const unit = b.basis === 'ms' ? 60000 : 1;
    const at = (p: number): number => Math.round(b.at(p) / unit) * unit;
    return { basis: b.basis, start: at(axis.from), end: at(axis.to) };
  }
  const t = nodeTimes(ctx, scene);
  const a = t.start ? timeValueOf(t.start) : null;
  if (!a) return null;
  const b = t.end ? timeValueOf(t.end) : null;
  return { basis: a.basis, start: a.value, end: b && b.basis === a.basis ? Math.max(a.value, b.value) : a.value };
}

/**
 * Erschlossene Zeit einer Szene ohne Zeit (PF 52, AP-24): zwischen dem Ende der vorigen und dem Beginn der nächsten Szene mit Zeit in
 * Lesereihenfolge – nur, wenn beide dieselbe Zeitrechnung haben und in Weltzeit in dieser Reihenfolge liegen (sonst erzählt die Szene
 * dazwischen vielleicht eine Rückblende). `after`/`before`: die Zeitangaben der Nachbarn (für Anzeige und Übernahme). Nicht gespeichert.
 */
export interface InferredTime {
  basis: WorldSpan['basis'];
  start: number;
  end: number;
  prevId: string;
  nextId: string;
  after: string;
  before: string;
}

const inferredCache = new WeakMap<StoryCtx, ReadonlyMap<string, InferredTime>>();

/** Alle erschlossenen Zeiten: je Szene ohne Zeit (eingeordnet), deren Nachbarn mit Zeit sie einrahmen */
export function inferredTimes(ctx: StoryCtx): ReadonlyMap<string, InferredTime> {
  const cached = inferredCache.get(ctx);
  if (cached) return cached;
  const out = new Map<string, InferredTime>();
  const scenes = narrativeOrder(ctx).scenes.map((s) => ({ scene: s.scene, span: worldSpanOf(ctx, s.scene) }));
  scenes.forEach((x, i) => {
    if (x.span) return;
    let p = i - 1;
    while (p >= 0 && !scenes[p]!.span) p--;
    let n = i + 1;
    while (n < scenes.length && !scenes[n]!.span) n++;
    const prev = scenes[p];
    const next = scenes[n];
    if (!prev?.span || !next?.span || prev.span.basis !== next.span.basis || prev.span.end > next.span.start) return;
    const pt = nodeTimes(ctx, prev.scene);
    const nt = nodeTimes(ctx, next.scene);
    const after = pt.end ?? pt.start;
    if (!after || !nt.start) return;
    out.set(x.scene.id, { basis: prev.span.basis, start: prev.span.end, end: next.span.start, prevId: prev.scene.id, nextId: next.scene.id, after, before: nt.start });
  });
  inferredCache.set(ctx, out);
  return out;
}

// ---------------------------------------------------------------------------
// Besetzung
// ---------------------------------------------------------------------------

/** Einträge, die an einer Szene beteiligt sind: Instanzen in ihrem Inneren (auch tiefer) und mit ihr verbundene Instanzen. */
export function participantsOf(ctx: StoryCtx, nodeId: string): Set<string> {
  const out = new Set<string>();
  for (const d of descendants(ctx, nodeId)) if (d.kind === 'instance' && d.entityId) out.add(d.entityId);
  for (const c of ctx.links.get(nodeId) ?? []) {
    const other = ctx.nodes.get(c.sourceId === nodeId ? c.targetId : c.sourceId);
    if (other?.entityId) out.add(other.entityId);
  }
  return out;
}

export interface SceneCast {
  /** Perspektivfigur (Eintrag) */
  pov: string | null;
  /** an der Szene beteiligt (in ihr bzw. mit ihr verbunden) */
  present: string[];
  /** in den Texten der Szene erwähnt, aber nicht beteiligt und nicht die Perspektive */
  mentioned: string[];
}

const isFoundational = (ctx: StoryCtx, entityId: string): boolean => {
  const e = ctx.entities.get(entityId);
  return !!e && ctx.types.get(e.typeId)?.category === 'foundational';
};

/** Einträge, auf die die Texte eines Nodes verweisen (ohne Foundationals und unbekannte Schlüssel). */
export function mentionedEntities(ctx: StoryCtx, node: StoryNode): Set<string> {
  const refs = refIndex(ctx);
  const out = new Set<string>();
  for (const text of textsOf(node)) {
    for (const m of mentionsIn(text)) {
      const e = refs.get(m.key);
      if (e && !isFoundational(ctx, e.id)) out.add(e.id);
    }
  }
  return out;
}

/** Perspektive einer Szene (Eintrag), sofern gesetzt und vorhanden. */
export const povOf = (ctx: StoryCtx, scene: StoryNode): string | null => {
  const v = scene.fields.pov;
  return typeof v === 'string' && ctx.entities.has(v) ? v : null;
};

/** Wer in einer Szene vorkommt: Perspektive, Beteiligte, im Text Erwähnte. */
export function sceneCast(ctx: StoryCtx, scene: StoryNode): SceneCast {
  const order = new Map(ctx.story.entities.map((e, i) => [e.id, i]));
  const sorted = (ids: Iterable<string>): string[] => [...ids].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
  const pov = povOf(ctx, scene);
  const present = participantsOf(ctx, scene.id);
  const mentioned = [...mentionedEntities(ctx, scene)].filter((id) => !present.has(id) && id !== pov);
  return { pov, present: sorted(present), mentioned: sorted(mentioned) };
}

// ---------------------------------------------------------------------------
// Auswertungen
// ---------------------------------------------------------------------------

export interface Anachrony {
  sceneId: string;
  /** Rückblende (spielt vor der vorigen Szene der Haupthandlung) bzw. Vorausdeutung (spielt nach der nächsten) */
  kind: 'flashback' | 'flashforward';
  /** Szene der Haupthandlung, gegen die gesprungen wird: bei der Rückblende die vorige, bei der Vorausdeutung die nächste */
  relativeTo: string;
}

/**
 * Rückblenden und Vorausdeutungen, abgeleitet aus Lesereihenfolge gegen Weltzeit. Die Haupthandlung ist die längste Folge von Szenen
 * (in Lesereihenfolge), die in der Weltzeit nicht zurückspringt – bei Gleichstand die mit den früher gelesenen Szenen. Eine Szene
 * außerhalb davon ist eine Rückblende, wenn sie endet, bevor die vorige Szene der Haupthandlung beginnt, und eine Vorausdeutung, wenn
 * sie beginnt, nachdem die nächste endet. Verglichen wird über Datumsangaben, ohne sie über die Lage auf demselben Zeitstrahl; Szenen
 * ohne Zeit zählen nicht. Ergebnis in Lesereihenfolge.
 */
export function anachronies(ctx: StoryCtx, order: NarrativeOrder = narrativeOrder(ctx)): Anachrony[] {
  const groups = new Map<string, { id: string; start: number; end: number }[]>();
  for (const { scene } of order.scenes) {
    const w = worldSpanOf(ctx, scene);
    if (w) groups.set(w.basis, [...(groups.get(w.basis) ?? []), { id: scene.id, start: w.start, end: w.end }]);
  }
  const index = new Map(order.scenes.map((s) => [s.scene.id, s.index]));
  return [...groups.values()].flatMap(anachroniesIn).sort((a, b) => index.get(a.sceneId)! - index.get(b.sceneId)!);
}

function anachroniesIn(list: { id: string; start: number; end: number }[]): Anachrony[] {
  // längste Folge, in der jede Szene nicht endet, bevor die vorige beginnt (Überlappung zählt als Fortgang)
  const len = list.map(() => 1);
  const prev = list.map(() => -1);
  for (let j = 0; j < list.length; j++) {
    for (let i = 0; i < j; i++) {
      if (list[j]!.end >= list[i]!.start && len[i]! + 1 > len[j]!) {
        len[j] = len[i]! + 1;
        prev[j] = i;
      }
    }
  }
  let last = -1;
  for (let j = 0; j < list.length; j++) if (last < 0 || len[j]! > len[last]!) last = j;
  const main = new Set<number>();
  for (let j = last; j >= 0; j = prev[j]!) main.add(j);

  const out: Anachrony[] = [];
  list.forEach((s, k) => {
    if (main.has(k)) return;
    let p = k - 1;
    while (p >= 0 && !main.has(p)) p--;
    let q = k + 1;
    while (q < list.length && !main.has(q)) q++;
    if (p >= 0 && s.end < list[p]!.start) out.push({ sceneId: s.id, kind: 'flashback', relativeTo: list[p]!.id });
    else if (q < list.length && s.start > list[q]!.end) out.push({ sceneId: s.id, kind: 'flashforward', relativeTo: list[q]!.id });
  });
  return out;
}

export type Presence = 'pov' | 'present' | 'mentioned';

export interface PresenceMatrix {
  /** Szenen in Lesereihenfolge */
  scenes: string[];
  /** je Eintrag eine Zeile (nach erstem Auftritt), Zellen parallel zu `scenes` */
  rows: { entityId: string; cells: (Presence | null)[] }[];
}

/** Wer in welcher Szene vorkommt (Perspektive, beteiligt, erwähnt). */
export function presenceMatrix(ctx: StoryCtx, order: NarrativeOrder = narrativeOrder(ctx)): PresenceMatrix {
  const scenes = order.scenes.map((s) => s.scene.id);
  const rows = new Map<string, (Presence | null)[]>();
  const cell = (entityId: string, i: number, p: Presence): void => {
    const row = rows.get(entityId) ?? Array<Presence | null>(scenes.length).fill(null);
    if (row[i] === null || row[i] === 'mentioned' || p === 'pov') row[i] = p;
    rows.set(entityId, row);
  };
  order.scenes.forEach(({ scene }, i) => {
    const cast = sceneCast(ctx, scene);
    for (const id of cast.mentioned) cell(id, i, 'mentioned');
    for (const id of cast.present) cell(id, i, 'present');
    if (cast.pov) cell(cast.pov, i, 'pov');
  });
  const first = (cells: (Presence | null)[]): number => cells.findIndex((c) => c !== null);
  const entityOrder = new Map(ctx.story.entities.map((e, i) => [e.id, i]));
  return {
    scenes,
    rows: [...rows]
      .map(([entityId, cells]) => ({ entityId, cells }))
      .sort((a, b) => first(a.cells) - first(b.cells) || (entityOrder.get(a.entityId) ?? 0) - (entityOrder.get(b.entityId) ?? 0)),
  };
}

// ---------------------------------------------------------------------------
// Auftrittsmatrix
// ---------------------------------------------------------------------------

/** Wie ein Element in einer Szene vorkommt, vom stärksten zum schwächsten: Perspektive, spricht (Bezug „Rede“), anwesend, erwähnt. */
export const APPEARANCE_LEVELS = ['pov', 'speaks', 'present', 'mentioned'] as const;
export type AppearanceLevel = (typeof APPEARANCE_LEVELS)[number];

/** Mit so vielen Szenen Abstand gilt eine Lücke als auffällig (die Figur verschwindet zu lange). */
export const LONG_GAP = 3;

export interface AppearanceRow {
  entityId: string;
  /** je Szene (parallel zu `scenes`) alle zutreffenden Stufen, die stärkste zuerst; leer: kommt nicht vor */
  cells: AppearanceLevel[][];
  /** Szenen, in denen das Element auftritt (Perspektive, spricht oder anwesend) bzw. nur erwähnt wird */
  onStage: number;
  mentioned: number;
  /** längste Strecke ohne Auftritt zwischen zwei Auftritten (Indizes der Szenen, einschließlich); null ohne Lücke */
  gap: { from: number; to: number } | null;
  /** Szenen nach dem letzten Auftritt bis zum Ende (null: tritt nie auf) */
  since: number | null;
}

export interface AppearanceMatrix {
  /** Szenen in der gewählten Reihenfolge; `label` „Kapitel.Szene“ (leer, wenn nicht eingeordnet) */
  scenes: { id: string; label: string; title: string }[];
  /** je Element eine Zeile, nach erstem Vorkommen */
  rows: AppearanceRow[];
}

const onStage = (cell: readonly AppearanceLevel[]): boolean => cell.some((l) => l !== 'mentioned');

/**
 * Auftrittsmatrix: Szenen mal Elemente. Reihenfolge `reading`: die eingeordneten Szenen in Lesereihenfolge; `world`: alle Szenen in der
 * Weltzeit (soweit ablesbar, siehe `worldOrder`). Perspektive und Beteiligte wie in `sceneCast`; „spricht“: Bezug mit der Art Rede in
 * einem Text der Szene; „erwähnt“: Verweis im Text. `include` beschränkt die Zeilen (z. B. auf Personen). Zu jedem Element die längste
 * Lücke zwischen zwei Auftritten und die Szenen seit dem letzten – so fällt auf, wer zu lange verschwindet.
 */
export function appearanceMatrix(ctx: StoryCtx, sequence: 'reading' | 'world' = 'reading', include?: (entityId: string) => boolean): AppearanceMatrix {
  const order = narrativeOrder(ctx);
  const labels = new Map(order.scenes.map((s) => [s.scene.id, s.label]));
  const list = sequence === 'reading' ? order.scenes.map((s) => s.scene) : worldOrder(ctx, scenesOf(ctx));
  const refs = refIndex(ctx);
  const rows = new Map<string, Set<AppearanceLevel>[]>();
  const mark = (entityId: string, i: number, level: AppearanceLevel): void => {
    if (include && !include(entityId)) return;
    const row = rows.get(entityId) ?? list.map(() => new Set<AppearanceLevel>());
    row[i]!.add(level);
    rows.set(entityId, row);
  };
  list.forEach((scene, i) => {
    const cast = sceneCast(ctx, scene);
    if (cast.pov) mark(cast.pov, i, 'pov');
    for (const id of cast.present) mark(id, i, 'present');
    for (const id of cast.mentioned) mark(id, i, 'mentioned');
    for (const text of textsOf(scene))
      for (const span of spansIn(text)) {
        if (span.role !== 'rede') continue;
        for (const key of span.keys) {
          const e = refs.get(key);
          if (e && !isFoundational(ctx, e.id)) mark(e.id, i, 'speaks');
        }
      }
  });
  const entityOrder = new Map(ctx.story.entities.map((e, i) => [e.id, i]));
  const out: (AppearanceRow & { first: number })[] = [...rows].map(([entityId, sets]) => {
    const cells = sets.map((s) => APPEARANCE_LEVELS.filter((l) => s.has(l)));
    const stage = cells.map((c, i) => (onStage(c) ? i : -1)).filter((i) => i >= 0);
    let gap: AppearanceRow['gap'] = null;
    for (let k = 1; k < stage.length; k++) {
      const from = stage[k - 1]! + 1;
      const to = stage[k]! - 1;
      if (to >= from && (!gap || to - from > gap.to - gap.from)) gap = { from, to };
    }
    return {
      entityId,
      cells,
      onStage: stage.length,
      mentioned: cells.filter((c) => c.length > 0 && !onStage(c)).length,
      gap,
      since: stage.length > 0 ? list.length - 1 - stage[stage.length - 1]! : null,
      first: cells.findIndex((c) => c.length > 0),
    };
  });
  out.sort((a, b) => a.first - b.first || (entityOrder.get(a.entityId) ?? 0) - (entityOrder.get(b.entityId) ?? 0));
  return {
    scenes: list.map((s) => ({ id: s.id, label: labels.get(s.id) ?? '', title: displayOf(ctx, s).title })),
    rows: out.map(({ first: _first, ...row }) => row),
  };
}

// ---------------------------------------------------------------------------
// Filter über den Graphen
// ---------------------------------------------------------------------------

/** Je Element die Szenen, in denen es vorkommt: als Perspektive, beteiligt (in ihr bzw. mit ihr verbunden) oder im Text erwähnt. */
export function scenesByEntity(ctx: StoryCtx): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const scene of scenesOf(ctx)) {
    const cast = sceneCast(ctx, scene);
    for (const id of [cast.pov, ...cast.present, ...cast.mentioned]) {
      if (!id) continue;
      const set = out.get(id) ?? new Set<string>();
      set.add(scene.id);
      out.set(id, set);
    }
  }
  return out;
}

/** UND: Szenen, in denen alle gewählten Elemente vorkommen („Wo begegnen sich Anna und Ben?“); ODER: mindestens eines. */
export type SceneFilterMode = 'and' | 'or';

/** Szenen, auf die der Filter passt; null, wenn nichts gewählt ist (dann filtert nichts). */
export function filterScenes(index: Map<string, Set<string>>, entityIds: readonly string[], mode: SceneFilterMode): Set<string> | null {
  if (entityIds.length === 0) return null;
  const sets = entityIds.map((id) => index.get(id) ?? new Set<string>());
  if (mode === 'or') return new Set(sets.flatMap((s) => [...s]));
  const [first, ...rest] = sets;
  return new Set([...first!].filter((id) => rest.every((s) => s.has(id))));
}

/** Je Szene: Erzählzeit (Wörter im Draft) und erzählte Zeit (Minuten zwischen Beginn und Ende, sofern sie eine Zeitrechnung hat). */
export function pacing(ctx: StoryCtx, order: NarrativeOrder = narrativeOrder(ctx)): { sceneId: string; words: number; minutes: number | null }[] {
  return order.scenes.map(({ scene }) => {
    const w = worldSpanOf(ctx, scene);
    const minutes = w?.basis === 'ms' ? (w.end - w.start) / 60000 : w?.basis === 'count' ? w.end - w.start : null;
    return { sceneId: scene.id, words: wordCount(draftOf(scene)), minutes: minutes === null ? null : Math.max(0, Math.round(minutes)) };
  });
}

/**
 * Node, an dem eine Linie zu `nodeId` auf der Fläche von `currentId` endet: der Node selbst, sonst sein nächster sichtbarer
 * Vorfahr (z. B. der Node, in dessen Innerem eine Szene liegt). null, wenn er außerhalb dieser Fläche liegt.
 */
export function visibleAnchor(ctx: StoryCtx, nodeId: string, currentId: string): string | null {
  const visible = new Set(visibleOn(ctx, currentId).map((n) => n.id));
  for (let cur = ctx.nodes.get(nodeId); cur; cur = cur.parentId ? ctx.nodes.get(cur.parentId) : undefined) {
    if (visible.has(cur.id)) return cur.id;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Export als ein Markdown-Dokument
// ---------------------------------------------------------------------------

const field = (n: StoryNode, key: string): string => {
  const v = n.fields[key];
  return typeof v === 'string' ? v.trim() : '';
};

/** Ein Stück des Manuskripts für den Export: Titel, Abschnitt, Kapitel, Text einer Szene (als Absätze und als Markdown), Szenenwechsel */
export type ManuscriptItem =
  | { kind: 'title'; text: string }
  | { kind: 'section'; text: string }
  | { kind: 'chapter'; number: number; title: string }
  | { kind: 'scene'; blocks: TextBlock[]; markdown: string }
  | { kind: 'break' };

/** Umfang des Exports: alles (leer), ein Abschnitt, ein Kapitel oder eine Szene */
export interface ManuscriptScope {
  sectionId?: string;
  chapterId?: string;
  sceneId?: string;
}

/**
 * Das Manuskript in Lesereihenfolge für den Export (PLAN.md §29): ohne Umfang das ganze Buch mit Titel und Abschnitten; ein Abschnitt mit
 * seiner Überschrift und seinen Kapiteln; ein Kapitel; eine Szene mit der Überschrift ihres Kapitels. Nicht eingeordnete und leere Szenen bleiben draußen; Verweise, Bezüge und Zeitmarken stehen als ihr Wortlaut da.
 */
export function manuscriptItems(ctx: StoryCtx, scope: ManuscriptScope = {}): { title: string; items: ManuscriptItem[] } {
  const order = narrativeOrder(ctx);
  const title = (order.manuscript && field(order.manuscript, 'title')) || displayOf(ctx, ctx.nodes.get(ctx.story.rootId)!).title;
  const whole = !scope.sectionId && !scope.chapterId && !scope.sceneId;
  const items: ManuscriptItem[] = whole ? [{ kind: 'title', text: title }] : [];
  let section: StoryNode | null = null;
  for (const ch of order.chapters) {
    const sectionStarts = ch.section !== null && ch.section !== section;
    section = ch.section;
    if (scope.sectionId && ch.section?.id !== scope.sectionId) continue;
    if (scope.chapterId && ch.chapter.id !== scope.chapterId) continue;
    if (scope.sceneId && !ch.scenes.some((s) => s.scene.id === scope.sceneId)) continue;
    if ((whole || scope.sectionId) && sectionStarts) items.push({ kind: 'section', text: field(ch.section!, 'title') });
    items.push({ kind: 'chapter', number: ch.number, title: field(ch.chapter, 'title') });
    const scenes = ch.scenes
      .filter((s) => !scope.sceneId || s.scene.id === scope.sceneId)
      .map((s) => ({ blocks: textBlocks(draftOf(s.scene)), markdown: plainRefs(draftOf(s.scene)).trim() }))
      .filter((x) => x.blocks.length > 0);
    scenes.forEach((x, i) => {
      if (i > 0) items.push({ kind: 'break' });
      items.push({ kind: 'scene', ...x });
    });
  }
  return { title, items };
}

/** Das Manuskript als eine Markdown-Datei: Titel, Abschnitte, Kapitel, Szenen (getrennt durch „* * *“), der Text wie geschrieben; Verweise als Text. */
export function manuscriptMarkdown(items: readonly ManuscriptItem[]): string {
  const out = items.map((i) => {
    if (i.kind === 'title' || i.kind === 'section') return `# ${i.text}`;
    if (i.kind === 'chapter') return `## ${i.number}.${i.title ? ` ${i.title}` : ''}`;
    if (i.kind === 'break') return '* * *';
    return i.markdown;
  });
  return `${out.join('\n\n')}\n`;
}

/** Ein Absatz als Klartext: Listen mit Spiegelstrich bzw. Nummer (eingerückt nach Ebene), Trennlinien als „* * *“ */
function plainBlock(b: TextBlock): string {
  if (b.kind === 'rule') return '* * *';
  const text = blockText(b);
  if (b.kind !== 'item') return text;
  return `${'  '.repeat(b.level ?? 0)}${b.number !== undefined ? `${b.number}.` : '–'} ${text}`;
}

/** zwei Einträge derselben Liste (beide mit bzw. beide ohne Nummer): ohne Leerzeile dazwischen */
const sameList = (a: TextBlock, b: TextBlock): boolean => a.kind === 'item' && b.kind === 'item' && (a.number === undefined) === (b.number === undefined);

/**
 * Das Manuskript als Klartext ohne Markierungen (PLAN.md §29): Titel und Abschnitte in Großbuchstaben, Kapitel als „1. Titel“, Absätze durch
 * eine Leerzeile getrennt (Einträge einer Liste untereinander), „* * *“ zwischen den Szenen.
 */
export function manuscriptPlain(items: readonly ManuscriptItem[]): string {
  const out = items.map((i) => {
    if (i.kind === 'title' || i.kind === 'section') return i.text.toLocaleUpperCase('de');
    if (i.kind === 'chapter') return `${i.number}.${i.title ? ` ${i.title}` : ''}`;
    if (i.kind === 'break') return '* * *';
    return i.blocks.map((b, k) => (k > 0 ? (sameList(i.blocks[k - 1]!, b) ? '\n' : '\n\n') : '') + plainBlock(b)).join('');
  });
  return `${out.join('\n\n')}\n`;
}

// ---------------------------------------------------------------------------
// Datenübernahme: Manuskript für ältere Projekte
// ---------------------------------------------------------------------------

export interface ManuscriptSetup {
  /** neue Nodes (Eltern vor Kindern): Manuskript, Kapitel */
  created: StoryNode[];
  /** Nodes, deren gesperrter Draft zu einem gewöhnlichen Dokument wurde */
  updated: StoryNode[];
}

const isDraft = (name: string): boolean => name.toLowerCase() === DRAFT.toLowerCase();

/**
 * Legt das Manuskript an, wenn es fehlt (ältere Projekte): mit einem Kapitel „Kapitel 1“. An Nodes, die keine Szenen sind, wird ein
 * gesperrter Draft zu einem gewöhnlichen Dokument. Die Szenen selbst übernimmt danach `migrateScenes` (geschriebene Drafts kommen ins
 * erste Kapitel). Liefert null, wenn es schon ein Manuskript gibt.
 */
export function ensureManuscript(ctx: StoryCtx, newId: () => string, now: string): ManuscriptSetup | null {
  if (manuscriptOf(ctx)) return null;
  const typeOf = (category: string) => {
    const t = ctx.story.types.find((x) => x.category === category);
    if (!t) throw new Error('Die Klassen der Erzählebene (Manuskript, Kapitel) fehlen.');
    return t;
  };
  const root = ctx.nodes.get(ctx.story.rootId)!;
  const base = { entityId: null, dynamicFields: [], tagColor: null, x: null, y: null, at: null, from: null, to: null, timeline: null, chapterId: null, narrates: [], narrateSkip: false, createdAt: now };
  const manuscript: StoryNode = { ...base, id: newId(), parentId: root.id, typeId: typeOf('manuscript').id, kind: 'manuscript', fields: { title: field(root, 'title') }, documents: [], rank: null };
  const chapter: StoryNode = { ...base, id: newId(), parentId: manuscript.id, typeId: typeOf('chapter').id, kind: 'chapter', fields: { title: 'Kapitel 1' }, documents: [], rank: 'i', colorSlot: 0 };
  const updated = ctx.story.nodes
    .filter((n) => !isScene(n) && n.kind !== 'scene' && n.documents.some((d) => isDraft(d.name) && d.locked))
    .map((n) => ({ ...n, documents: n.documents.map((d) => (isDraft(d.name) ? { ...d, locked: false } : d)) }));
  return { created: [manuscript, chapter], updated };
}

export interface SectionAdoption {
  /** neue Abschnitte (jeder an der Stelle des Kapitels, mit dem sein Teil begann) */
  created: StoryNode[];
  /** Kapitel: in ihren Abschnitt gerückt bzw. ohne das Feld „part“ */
  updated: StoryNode[];
  /** die vordefinierte Klasse Kapitel ohne das Feld „Teil / Akt“ (null: hatte es nicht) */
  chapterType: NodeType | null;
}

/**
 * Datenübernahme (Abschnitte, PLAN.md §42): Das frühere Feld „Teil / Akt“ der Kapitel markierte, wo ein neuer Teil beginnt – daraus werden
 * Abschnitte. Ein Kapitel mit einem Teil beginnt einen Abschnitt dieses Namens (außer er heißt wie der laufende); die folgenden Kapitel
 * ohne Teil gehören dazu; Kapitel vor dem ersten Teil bleiben direkt im Manuskript. Danach hat kein Kapitel mehr das Feld. null: nichts
 * zu tun.
 */
export function partsToSections(ctx: StoryCtx, sectionTypeId: string, newId: () => string, now: string): SectionAdoption | null {
  const manuscript = manuscriptOf(ctx);
  const type = ctx.story.types.find((t) => t.builtin && t.category === 'chapter');
  const chapterType = type?.fields.some((f) => f.key === 'part') ? { ...type, fields: type.fields.filter((f) => f.key !== 'part') } : null;
  const withPart = ctx.story.nodes.filter((n) => n.kind === 'chapter' && 'part' in n.fields);
  if (!manuscript || (withPart.length === 0 && !chapterType)) return null;
  const created: StoryNode[] = [];
  const updated = new Map<string, StoryNode>();
  const runs: { section: StoryNode; chapters: StoryNode[] }[] = [];
  for (const ch of byRank(childrenOf(ctx, manuscript.id).filter((c) => c.kind === 'chapter'))) {
    const part = field(ch, 'part');
    const current = runs[runs.length - 1];
    if (part !== '' && part !== (current ? field(current.section, 'title') : '')) {
      const section: StoryNode = { ...newSection(sectionTypeId, manuscript.id, ch.rank!, newId, now), fields: { title: part } };
      created.push(section);
      runs.push({ section, chapters: [ch] });
    } else if (current) current.chapters.push(ch);
  }
  for (const run of runs) {
    const ranks = evenRanks(run.chapters.length);
    run.chapters.forEach((ch, i) => updated.set(ch.id, { ...ch, parentId: run.section.id, rank: ranks[i]! }));
  }
  for (const ch of withPart) {
    const { part: _part, ...fields } = (updated.get(ch.id) ?? ch).fields;
    updated.set(ch.id, { ...(updated.get(ch.id) ?? ch), fields });
  }
  return { created, updated: [...updated.values()], chapterType };
}

// ---------------------------------------------------------------------------
// Szenenkontext (Vorbereitung der KI, noch ohne Prompt)
// ---------------------------------------------------------------------------

export interface EntityInfo {
  refKey: string;
  type: string;
  title: string;
  /** statische Felder des Eintrags (Beschriftung → Wert, ohne Bilder, Verweise als reiner Text) */
  fields: Record<string, string | number>;
  /** dynamische Felder seiner Instanzen in der Szene */
  dynamic: Record<string, string | number>;
}

export interface SceneContext {
  foundationals: EntityInfo[];
  manuscript: { title: string; voice: string; tense: string };
  scene: {
    id: string;
    /** „Kapitel.Szene“; leer, wenn die Szene nicht eingeordnet ist */
    label: string;
    title: string;
    description: string;
    goal: string;
    conflict: string;
    outcome: string;
    status: string;
    pov: EntityInfo | null;
    /** Weltzeit (Datum), sofern bekannt */
    start: string | null;
    end: string | null;
    draft: string;
    /** das Material der Szene (PLAN.md §36): Ideen mit ihrem Text, Links mit Adresse, Bilder mit Titel */
    material: { kind: MaterialItem['kind']; title: string; text: string }[];
    /** Metadaten und andere Notizen in ihrem Inneren */
    notes: { title: string; text: string }[];
  };
  cast: { present: EntityInfo[]; mentioned: EntityInfo[] };
  /** Was der Leser bisher gelesen hat: frühere Szenen in Lesereihenfolge mit ihrer Kurzbeschreibung (sonst Zusammenfassung) */
  readSoFar: { label: string; title: string; summary: string }[];
  /** Weltzeit um die Szene: andere Szenen unmittelbar davor und danach (nach Datum, sonst auf ihrem Zeitstrahl) */
  chronology: { before: { title: string; start: string | null }[]; after: { title: string; start: string | null }[] };
}

const textValue = (v: FieldValue | undefined): string | number | null => (typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? plainRefs(v.trim()) : null);

/**
 * Name eines Elements: der Wert im Titelfeld seiner Klasse, getrimmt; leer, wenn keiner eingetragen ist (den Rückfall – Schlüssel,
 * Klassenname … – wählt der Aufrufer).
 */
export function entityName(e: { fields: Record<string, FieldValue> } | null | undefined, type: { titleKey: string } | null | undefined): string {
  if (!e || !type) return '';
  return String(e.fields[type.titleKey] ?? '').trim();
}

/** Name eines Eintrags (Titelfeld seiner Klasse, sonst sein Schlüssel). */
export function entityTitle(ctx: StoryCtx, entityId: string): string {
  const e = ctx.entities.get(entityId);
  if (!e) return '';
  return entityName(e, ctx.types.get(e.typeId)) || e.refKey;
}

function entityInfo(ctx: StoryCtx, entityId: string, within: StoryNode[] = []): EntityInfo {
  const e = ctx.entities.get(entityId)!;
  const type = ctx.types.get(e.typeId)!;
  const fields: Record<string, string | number> = {};
  for (const def of type.fields) {
    if (def.type === 'image') continue;
    const raw = e.fields[def.key];
    const v = def.type === 'ref' ? (typeof raw === 'string' && ctx.entities.has(raw) ? entityTitle(ctx, raw) : null) : textValue(raw);
    if (v !== null) fields[def.label] = v;
  }
  const dynamic: Record<string, string | number> = {};
  for (const n of within.flatMap((w) => descendants(ctx, w.id))) {
    if (n.entityId !== entityId) continue;
    for (const f of n.dynamicFields) {
      const v = f.type === 'image' ? null : textValue(f.value);
      if (v !== null) dynamic[f.label] = v;
    }
  }
  return { refKey: e.refKey, type: type.name, title: entityTitle(ctx, entityId), fields, dynamic };
}

/** Eine Notiz im Material der Szene nach ihrem Titel (etwa die frühere „Zusammenfassung“ eines Ereignisses) */
const noteText = (n: StoryNode, title: string): string => plainRefs(n.material?.flatMap((m) => (m.kind === 'note' && m.title === title ? [m.text.trim()] : []))[0] ?? '');

/** Notizen im Inneren eines Nodes (normale Nodes wie Metadaten): Titel und ihre Felder als Text. */
function notesIn(ctx: StoryCtx, nodeId: string): { title: string; text: string }[] {
  return childrenOf(ctx, nodeId)
    .filter((c) => c.kind === 'normal')
    .map((c) => {
      const f = resolveFields(ctx, c);
      const type = ctx.types.get(c.typeId)!;
      const lines = [
        ...type.fields.filter((d) => d.key !== type.titleKey && d.type !== 'image').map((d) => [d.label, textValue(f[d.key])] as const),
        ...c.dynamicFields.filter((d) => d.type !== 'image').map((d) => [d.label, textValue(d.value)] as const),
      ].filter(([, v]) => v !== null);
      return { title: displayOf(ctx, c).title, text: lines.map(([k, v]) => `${k}: ${v}`).join('\n') };
    });
}

/** Andere Szenen unmittelbar vor und nach einer Szene in der Weltzeit (je höchstens zwei). */
function chronologyOf(ctx: StoryCtx, scene: StoryNode): SceneContext['chronology'] {
  const own = worldSpanOf(ctx, scene);
  if (!own) return { before: [], after: [] };
  const others = (own.basis === 'ms' || own.basis === 'count' ? scenesOf(ctx) : orderedItems(ctx, own.basis))
    .filter((n) => n.id !== scene.id)
    .map((n) => ({ n, w: worldSpanOf(ctx, n) }))
    .filter((x): x is { n: StoryNode; w: WorldSpan } => x.w?.basis === own.basis)
    .sort((a, b) => a.w.start - b.w.start || a.w.end - b.w.end || cmpStr(a.n.createdAt, b.n.createdAt) || cmpStr(a.n.id, b.n.id));
  const info = ({ n }: { n: StoryNode }) => ({ title: displayOf(ctx, n).title, start: nodeTimes(ctx, n).start });
  return {
    before: others.filter((x) => x.w.end < own.start).slice(-2).map(info),
    after: others.filter((x) => x.w.start > own.end).slice(0, 2).map(info),
  };
}

/** Alles, was eine KI zum Schreiben einer Szene wissen muss, strukturiert und rein aus den gespeicherten Daten. */
export function sceneContext(ctx: StoryCtx, sceneId: string): SceneContext {
  const scene = ctx.nodes.get(sceneId);
  if (!scene || !isScene(scene)) throw new Error('Die Szene existiert nicht.');
  const order = narrativeOrder(ctx);
  const entry = order.scenes.find((s) => s.scene.id === sceneId);
  const cast = sceneCast(ctx, scene);
  const time = sceneTime(ctx, scene);

  const readSoFar = (entry ? order.scenes.slice(0, entry.index) : []).map(({ scene: s, label }) => ({
    label,
    title: displayOf(ctx, s).title,
    summary: plainRefs(field(s, 'description')) || noteText(s, 'Zusammenfassung'),
  }));

  const manuscript = order.manuscript;
  return {
    foundationals: ctx.foundationals.map((f) => entityInfo(ctx, f.id)),
    manuscript: { title: manuscript ? field(manuscript, 'title') : '', voice: manuscript ? field(manuscript, 'voice') : '', tense: manuscript ? field(manuscript, 'tense') : '' },
    scene: {
      id: scene.id,
      label: entry?.label ?? '',
      title: displayOf(ctx, scene).title,
      description: plainRefs(field(scene, 'description')),
      goal: plainRefs(field(scene, 'goal')),
      conflict: plainRefs(field(scene, 'conflict')),
      outcome: plainRefs(field(scene, 'outcome')),
      status: field(scene, 'status'),
      pov: cast.pov ? entityInfo(ctx, cast.pov, [scene]) : null,
      start: time.start,
      end: time.end,
      draft: draftOf(scene),
      material: (scene.material ?? []).map((m) => ({ kind: m.kind, title: m.title, text: (m.kind === 'note' ? m.text : m.kind === 'link' ? m.url : '').trim() })),
      notes: notesIn(ctx, scene.id),
    },
    cast: { present: cast.present.map((id) => entityInfo(ctx, id, [scene])), mentioned: cast.mentioned.map((id) => entityInfo(ctx, id)) },
    readSoFar,
    chronology: chronologyOf(ctx, scene),
  };
}

// ---------------------------------------------------------------------------
// Element-Kontext beim Schreiben
// ---------------------------------------------------------------------------

/** Ein Auftritt eines Elements: Szene in Lesereihenfolge und wie es darin vorkommt. */
export interface Appearance {
  sceneId: string;
  /** „Kapitel.Szene“ */
  label: string;
  title: string;
  presence: Presence;
}

/** Was das Panel an einem verknüpften Wort zeigt: Steckbrief, Bild, letzte Auftritte, Angaben nur für die Szene des Textes. */
export interface EntityGlance {
  entityId: string;
  refKey: string;
  title: string;
  typeName: string;
  typeColor: string;
  /** eigenes Bild, sonst das Standardbild der Klasse */
  imageId: string | null;
  /** Kurzbeschreibung (Hover-Feld der Klasse) als reiner Text */
  summary: string;
  /** übrige ausgefüllte Felder der Klasse (ohne Titel, Kurzbeschreibung und Bilder) */
  fields: { label: string; value: string }[];
  /** die Szene des Textes; null, wenn der Text zu keiner Szene gehört */
  scene: {
    /** „Kapitel.Szene“; leer, wenn die Szene nicht eingeordnet ist */
    label: string;
    /** wie das Element in dieser Szene vorkommt (null: noch gar nicht) */
    presence: Presence | null;
    /** eigene Angaben der Kopie des Elements in der Szene (bei mehreren Kopien alle) */
    fields: { label: string; value: string }[];
  } | null;
  /**
   * Die letzten Auftritte in Lesereihenfolge (älteste zuerst): vor der Szene des Textes, wenn sie eingeordnet ist, sonst die letzten
   * überhaupt. Die Szene selbst zählt nie mit.
   */
  recent: Appearance[];
  /** Auftritte vor der Szene (bzw. insgesamt), auch die nicht mehr in `recent` gezeigten */
  earlier: number;
}

/** Wie ein Element in einer Szene vorkommt: Perspektive, beteiligt (in ihr bzw. mit ihr verbunden) oder im Text erwähnt. */
function presenceIn(ctx: StoryCtx, scene: StoryNode, entityId: string, refs: Map<string, { id: string }>): Presence | null {
  if (povOf(ctx, scene) === entityId) return 'pov';
  if (participantsOf(ctx, scene.id).has(entityId)) return 'present';
  for (const text of textsOf(scene)) for (const m of mentionsIn(text)) if (refs.get(m.key)?.id === entityId) return 'mentioned';
  return null;
}

const shown = (v: string | number | null): string | null => (v === null ? null : String(v));

/**
 * Element-Kontext an einem verknüpften Wort: Steckbrief (Bild, Kurzbeschreibung, übrige Felder), die letzten `limit` Auftritte in
 * Lesereihenfolge und – wenn der Text zu einer Szene gehört – wie das Element darin vorkommt und seine Angaben nur für diese Szene.
 */
export function entityGlance(ctx: StoryCtx, entityId: string, sceneId: string | null, limit = 3, order: NarrativeOrder = narrativeOrder(ctx)): EntityGlance | null {
  const e = ctx.entities.get(entityId);
  const type = e ? ctx.types.get(e.typeId) : undefined;
  if (!e || !type) return null;
  const fields = type.fields
    .filter((d) => d.key !== type.titleKey && d.key !== type.hoverKey && d.type !== 'image')
    .map((d) => {
      const raw = e.fields[d.key];
      const v = d.type === 'ref' ? (typeof raw === 'string' && ctx.entities.has(raw) ? entityTitle(ctx, raw) : null) : shown(textValue(raw));
      return v === null ? null : { label: d.label, value: v };
    })
    .filter((f): f is { label: string; value: string } => f !== null);

  const refs = refIndex(ctx);
  const scene = sceneId ? ctx.nodes.get(sceneId) : undefined;
  const at = scene ? order.scenes.findIndex((s) => s.scene.id === scene.id) : -1;
  const before = (at >= 0 ? order.scenes.slice(0, at) : order.scenes).filter((s) => s.scene.id !== sceneId);
  const appearances: Appearance[] = [];
  for (const s of before) {
    const presence = presenceIn(ctx, s.scene, entityId, refs);
    if (presence) appearances.push({ sceneId: s.scene.id, label: s.label, title: displayOf(ctx, s.scene).title, presence });
  }

  const own =
    scene && isScene(scene)
      ? {
          label: at >= 0 ? order.scenes[at]!.label : '',
          presence: presenceIn(ctx, scene, entityId, refs),
          fields: descendants(ctx, scene.id)
            .filter((n) => n.kind === 'instance' && n.entityId === entityId)
            .flatMap((n) => n.dynamicFields)
            .map((f) => ({ label: f.label, value: f.type === 'image' ? null : shown(textValue(f.value)) }))
            .filter((f): f is { label: string; value: string } => f.value !== null),
        }
      : null;

  return {
    entityId,
    refKey: e.refKey,
    title: entityTitle(ctx, entityId),
    typeName: type.name,
    typeColor: type.color,
    imageId: imageIdOf(type, e.fields, e.colorSlot, e.id),
    summary: type.hoverKey ? (shown(textValue(e.fields[type.hoverKey])) ?? '') : '',
    fields,
    scene: own,
    recent: appearances.slice(-limit),
    earlier: appearances.length,
  };
}
